/**
 * First-run empty state — prototype/integrations-frames.js frame 11 (`set(10,...)`).
 * Picking a provider here is the choice itself (a `.pk` row, not a brand
 * sign-in button — those only appear once inside the connect page).
 */
import React, { useMemo } from "react";
import ProviderPickRow from "./ProviderPickRow";
import appConfig from "../../../../appConfig";
import styles from "../IntegrationsPage.module.scss";
import { INTEGRATION_NAMES, IT } from "../../../../consts";

const STEPS = [
  {
    title: "Sign in",
    description: "You approve read-only access on your provider's own screen.",
  },
  {
    title: "Choose what to sync",
    description:
      "Tick the items that should be public. You can change this later.",
  },
  {
    title: "Data appears",
    description:
      "The first sync takes a minute or two, then it keeps itself current.",
  },
];

// Static — created once, not re-created per render.
const BETA_BADGE = { label: "Beta", color: "blueLight" };

// Describes the two picks; onClick is attached per-instance below since it
// needs the onSelectProvider prop — everything else here never changes.
const PICKS_CONFIG = [
  {
    provider: "google",
    title: `Connect ${INTEGRATION_NAMES[IT.google]}`,
    description: "Any calendar on your Google account, including shared ones.",
  },
  {
    provider: "outlook",
    title: `Connect ${INTEGRATION_NAMES[IT.outlook]}`,
    description: "Outlook and Microsoft 365.",
    badge: BETA_BADGE,
  },
];

// STEPS never changes, so the rendered list is built once at module scope
// rather than re-mapped inline in JSX on every render.
const STEP_ITEMS = STEPS.map((step, i) => (
  <li key={step.title}>
    <span className={styles.emptyStepHead}>
      <span className={styles.emptyStepNumber}>{i + 1}</span>
      {step.title}
    </span>
    <p>{step.description}</p>
  </li>
));

const EmptyStateFirstRun = ({ onSelectProvider }) => {
  // Memoized as a unit (not built inline in the .map() below) so each
  // row's onClick keeps a stable reference across renders — the same
  // concern useCallback exists for, just applied to a whole array at once,
  // matching RowMenu.jsx's items useMemo.
  const pickRows = useMemo(
    () =>
      PICKS_CONFIG.map((pick) => (
        <ProviderPickRow
          key={pick.provider}
          provider={pick.provider}
          title={pick.title}
          description={pick.description}
          badge={pick.badge}
          onClick={() => onSelectProvider(pick.provider)}
        />
      )),
    [onSelectProvider]
  );

  return (
    <div className={styles.emptyState}>
      <h3 className={styles.emptyTitle}>
        Bring your data into {appConfig.displayName}
      </h3>
      <p className={styles.emptyBody}>
        Pick where your data live. {appConfig.displayName} reads them, keeps
        them in sync as they change, and your widgets fill themselves in.
      </p>
      <div className={styles.picks}>{pickRows}</div>
      <ol className={styles.emptySteps}>{STEP_ITEMS}</ol>
    </div>
  );
};

export default EmptyStateFirstRun;
